import { getBcp47, type TSupportedLocale } from "./locales";

/**
 * Currency configuration per locale. Used to provide the correct
 * symbol and ISO code when rendering prices in translation strings.
 */
export interface ILocaleCurrency {
  /** ISO 4217 code, e.g. "GBP" or "EUR". */
  code: string;
  /** Display symbol, e.g. "£" or "€". */
  symbol: string;
}

const LOCALE_CURRENCY: Record<TSupportedLocale, ILocaleCurrency> = {
  en: { code: "GBP", symbol: "£" },
  de: { code: "EUR", symbol: "€" },
};

/** Returns the currency config for a given locale. */
export const getLocaleCurrency = (locale: TSupportedLocale): ILocaleCurrency => (
  LOCALE_CURRENCY[locale]
);

/** Shorthand: returns just the currency symbol for a locale. */
export const getCurrencySymbol = (locale: TSupportedLocale): string => (
  LOCALE_CURRENCY[locale].symbol
);

/**
 * Extends the built-in `Intl.NumberFormatOptions` with newer properties
 * that our runtime supports but the project's TS lib target omits.
 */
interface IExtendedNumberFormatOptions extends Intl.NumberFormatOptions {
  trailingZeroDisplay?: "auto" | "stripIfInteger";
}

/**
 * Builds an `Intl.NumberFormat` currency formatter for the given locale.
 * When `compact` is true, trailing zeros are stripped from whole numbers
 * (e.g. "£10" instead of "£10.00").
 */
const buildFormatter = (locale: TSupportedLocale, compact: boolean): Intl.NumberFormat => {
  const { code } = getLocaleCurrency(locale);
  const options: IExtendedNumberFormatOptions = {
    style: "currency",
    currency: code,
    ...(compact && { trailingZeroDisplay: "stripIfInteger" as const }),
  };
  return new Intl.NumberFormat(getBcp47(locale), options);
};

/**
 * Formats a numeric price as a locale-aware currency string using
 * `Intl.NumberFormat`. Handles symbol, position, spacing, and decimal
 * separators automatically.
 *
 * @example
 * formatCurrency(10, "en")  // "£10.00"
 * formatCurrency(10, "de")  // "10,00 €"
 */
export const formatCurrency = (
  value: number | string,
  locale: TSupportedLocale,
): string => buildFormatter(locale, false).format(Number(value));

/**
 * Like `formatCurrency` but drops trailing zeros so that whole-number
 * prices read naturally (e.g. "£10" instead of "£10.00").
 */
export const formatCurrencyCompact = (
  value: number | string,
  locale: TSupportedLocale,
): string => buildFormatter(locale, true).format(Number(value));

const PRICE_FALLBACK = "--";

/**
 * Formats a currency value with null/NaN fallback handling.
 * Returns `"£--"` (or the locale equivalent) for missing or invalid values
 * instead of formatting zero.
 *
 * @param compact When true, strips trailing zeros from whole numbers.
 *
 * @example
 * formatCurrencyWithFallback(19, "en")        // "£19.00"
 * formatCurrencyWithFallback(undefined, "en")  // "£--"
 * formatCurrencyWithFallback(10, "en", true)   // "£10"
 */
export const formatCurrencyWithFallback = (
  value: number | string | null | undefined,
  locale: TSupportedLocale,
  compact = false,
): string => {
  if (value == null || Number.isNaN(Number(value))) {
    return `${getCurrencySymbol(locale)}${PRICE_FALLBACK}`;
  }
  return compact
    ? formatCurrencyCompact(value, locale)
    : formatCurrency(value, locale);
};
